import { theme as antdTheme, type ThemeConfig } from 'antd';

/**
 * Тема «Линия» — плотный светлый/тёмный рабочий инструмент.
 *
 * Палитра живёт в CSS-переменных (index.css): инлайновые стили страниц берут
 * значения через `L.*` = var(--l-*) и переключаются темой автоматически.
 * Для токенов antd нужны реальные hex — их отдаёт makeTheme(mode).
 */

export type ThemeMode = 'light' | 'dark';

// Для инлайновых стилей страниц — всегда через переменные.
export const L = {
  bg: 'var(--l-bg)',
  side: 'var(--l-side)',
  surface: 'var(--l-surface)',
  ink: 'var(--l-ink)',
  text: 'var(--l-text)',
  muted: 'var(--l-muted)',
  faint: 'var(--l-faint)',
  line: 'var(--l-line)',
  lineSoft: 'var(--l-line-soft)',
  fill: 'var(--l-fill)',
  hover: 'var(--l-hover)',
  accent: 'var(--l-accent)',
  accentBg: 'var(--l-accent-bg)',
  ok: 'var(--l-ok)',
  okBg: 'var(--l-ok-bg)',
  warn: 'var(--l-warn)',
  warnBg: 'var(--l-warn-bg)',
  danger: 'var(--l-danger)',
  dangerBg: 'var(--l-danger-bg)',
  violet: 'var(--l-violet)',
  teal: 'var(--l-teal)',
  shadow: 'var(--l-shadow)',
  mono: "'IBM Plex Mono', 'SF Mono', Consolas, monospace",
};

// Реальные значения обеих тем (источник правды — продублирован в index.css).
export const PALETTES: Record<ThemeMode, Record<string, string>> = {
  light: {
    bg: '#F2F3F5',
    side: '#ECEEF1',
    surface: '#FFFFFF',
    ink: '#16181D',
    text: '#3C4147',
    muted: '#70767E',
    faint: '#9AA0A8',
    line: '#E2E4E8',
    lineSoft: '#ECEEF1',
    fill: '#F0F1F3',
    hover: '#F7F8FA',
    accent: '#0B62D6',
    accentBg: '#E9F0FB',
    ok: '#178A50',
    warn: '#B4690E',
    danger: '#C6363C',
    violet: '#7C5CD6',
    teal: '#3AA9A0',
    border: '#DCDFE3',
  },
  dark: {
    bg: '#0F1114',
    side: '#0C0E11',
    surface: '#16191E',
    ink: '#E8EAED',
    text: '#C6CBD2',
    muted: '#8D939C',
    faint: '#6A7178',
    line: '#26292F',
    lineSoft: '#1E2126',
    fill: '#1C2026',
    hover: '#1A1D22',
    accent: '#5C9DFF',
    accentBg: 'rgba(92, 157, 255, 0.13)',
    ok: '#41B478',
    warn: '#D99A45',
    danger: '#E06A6F',
    violet: '#9D8CFF',
    teal: '#4FBDB4',
    border: '#2E3238',
  },
};

export function makeTheme(mode: ThemeMode): ThemeConfig {
  const p = PALETTES[mode];
  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: p.accent,
      colorSuccess: p.ok,
      colorWarning: p.warn,
      colorError: p.danger,
      colorInfo: p.accent,
      colorText: p.ink,
      colorTextSecondary: p.muted,
      colorTextTertiary: p.faint,
      colorBorder: p.border,
      colorBorderSecondary: p.lineSoft,
      borderRadius: 7,
      fontFamily: "'Golos Text', -apple-system, 'Segoe UI', sans-serif",
      fontSize: 13,
      colorBgContainer: p.surface,
      colorBgLayout: p.bg,
      colorBgElevated: mode === 'dark' ? '#1B1F25' : '#FFFFFF',
      controlHeight: 30,
      controlHeightSM: 24,
      controlHeightLG: 36,
      // Заголовки страниц компактные: старые <Title level={3}> не должны кричать
      fontSizeHeading3: 16,
      fontSizeHeading4: 14,
      fontSizeHeading5: 13,
      // Быстрые и упругие анимации (ease-out, до 250 мс)
      motionDurationFast: '0.1s',
      motionDurationMid: '0.15s',
      motionDurationSlow: '0.25s',
      motionEaseOut: 'cubic-bezier(0.23, 1, 0.32, 1)',
      motionEaseInOut: 'cubic-bezier(0.77, 0, 0.175, 1)',
    },
    components: {
      Button: {
        primaryShadow: 'none',
        defaultShadow: 'none',
        fontWeight: 500,
        paddingInline: 12,
      },
      Card: {
        borderRadiusLG: 10,
        headerHeight: 40,
        headerFontSize: 13,
        boxShadowTertiary: 'none',
        paddingLG: 16,
      },
      Table: {
        borderRadius: 10,
        headerBg: mode === 'dark' ? '#14171B' : '#FAFBFC',
        headerColor: p.faint,
        headerSplitColor: 'transparent',
        cellPaddingBlock: 9,
        cellPaddingInline: 16,
        fontSize: 12.5,
        rowHoverBg: p.hover,
      },
      Modal: {
        borderRadiusLG: 12,
        titleFontSize: 14,
      },
      Drawer: {
        paddingLG: 16,
      },
      Menu: {
        itemBorderRadius: 6,
        subMenuItemBorderRadius: 6,
        itemHeight: 30,
        itemMarginBlock: 1,
        itemMarginInline: 8,
        groupTitleFontSize: 10,
        fontSize: 12.5,
        itemColor: p.text,
        itemHoverBg: mode === 'dark' ? '#181C21' : '#E4E7EA',
        itemHoverColor: p.ink,
        itemSelectedBg: p.accentBg,
        itemSelectedColor: p.accent,
        activeBarBorderWidth: 0,
      },
      Tag: {
        borderRadiusSM: 5,
        fontSizeSM: 11,
      },
      Tabs: {
        titleFontSize: 12.5,
        horizontalItemPadding: '10px 0',
        horizontalItemGutter: 24,
      },
      Statistic: {
        contentFontSize: 19,
        titleFontSize: 11,
      },
      Segmented: {
        itemSelectedBg: mode === 'dark' ? '#E8EAED' : '#16181D',
        itemSelectedColor: mode === 'dark' ? '#16181D' : '#FFFFFF',
      },
      Form: {
        itemMarginBottom: 14,
        labelFontSize: 12,
        verticalLabelPadding: '0 0 4px',
      },
    },
  };
}

// Совместимость: страницы, импортирующие тему по умолчанию (не используется в App).
const defaultTheme = makeTheme('light');
export default defaultTheme;
